import { CaretDown } from '@app/components/icons/caret-down';
import { CheckboxIcon } from '@app/components/icons/checkbox';
import { InfoCircle } from '@app/components/icons/info-circle';
import { ReplayIcon } from '@app/components/icons/replay';
import { Spinner } from '@app/components/icons/spinner';
import { Tooltip } from '@app/components/tooltip';
import { Session } from '@app/routes/content/routes/library/components/icons/session-icon';
import { Stage } from '@app/routes/content/routes/library/components/icons/stage-icon';
import { Listbox } from '@headlessui/react';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useLayer } from 'react-laag';

import { getEventPartOptionLabel } from './constants';
import type { EventPartOption, VideoArea } from './types';

type EventPartsComboboxProps = {
  hasError?: boolean;
  isLoadingOptions?: boolean;
  onChange: (option: EventPartOption) => void;
  onOpen: () => void;
  options: EventPartOption[];
  placeholder: string;
  value: EventPartOption | null;
};

export function EventPartsCombobox({
  hasError = false,
  isLoadingOptions = false,
  onChange,
  onOpen,
  options,
  placeholder,
  value,
}: EventPartsComboboxProps) {
  return (
    <Listbox
      by={(a, b) =>
        a?.eventPartId === b?.eventPartId &&
        a?.eventPartType === b?.eventPartType
      }
      onChange={onChange}
      value={value}
    >
      {({ open }) => (
        <EventPartsComboboxContent
          hasError={hasError}
          isOpen={open}
          isLoadingOptions={isLoadingOptions}
          onOpen={onOpen}
          options={options}
          placeholder={placeholder}
          value={value}
        />
      )}
    </Listbox>
  );
}

const EventPartsComboboxContent = ({
  hasError,
  isOpen,
  isLoadingOptions,
  onOpen,
  options,
  placeholder,
  value,
}: {
  hasError: boolean;
  isOpen: boolean;
  isLoadingOptions: boolean;
  onOpen: () => void;
  options: EventPartOption[];
  placeholder: string;
  value: EventPartOption | null;
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dropdownWidth, setDropdownWidth] = useState<number>();
  const { renderLayer, triggerProps, layerProps } = useLayer({
    isOpen,
    placement: 'bottom-start',
    triggerOffset: 6,
    auto: true,
  });

  useEffect(
    function requestOptionsOnOpen() {
      if (!isOpen) return;

      onOpen();
    },
    [isOpen, onOpen],
  );

  useEffect(
    function setInitialDropdownWidth() {
      if (!containerRef.current || dropdownWidth) return;

      setDropdownWidth(containerRef.current.getBoundingClientRect().width);
    },
    [dropdownWidth],
  );

  return (
    <div ref={containerRef} className="relative flex w-full items-center gap-2">
      <Listbox.Button
        {...triggerProps}
        className={cx(
          'relative flex min-w-0 flex-1 items-center justify-between gap-2 overflow-hidden rounded-10',
          'border border-gray-700 py-3 px-4 outline-none bg-spring-neutral-base',
          'outline-none ui-focus-visible:ring-2 ui-focus-visible:ring-blue-500 ui-focus-visible:ring-offset-2 ui-focus-visible:ring-offset-spring-neutral-base',
          {
            'border-[#F27676]': hasError,
          },
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          {value && <EventPartTypeBadge type={value.eventPartType} />}

          <span
            className={cx('truncate text-sm font-regular', {
              'text-spring-neutral-b3': !value,
              'text-spring-neutral-b2': value,
            })}
          >
            {value ? getEventPartOptionLabel(value) : placeholder}
          </span>
        </span>

        {isLoadingOptions ? (
          <span aria-hidden="true">
            <Spinner
              className="shrink-0 text-spring-neutral-b3"
              size="tiny"
              theme="dark"
            />
          </span>
        ) : (
          <CaretDown
            className={cx(
              'h-5 w-5 shrink-0 text-spring-neutral-b3 transition-transform duration-200',
              { 'rotate-180': isOpen },
            )}
            aria-hidden="true"
          />
        )}
      </Listbox.Button>

      {options.some(option => option.kind === 'replay') && (
        <Tooltip
          tooltip={t('content-recording.featured-replay-area-tooltip')}
          placement="top-center"
        >
          <button
            type="button"
            aria-label={t('content-recording.featured-replay-area-info-label')}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-spring-neutral-b3 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <InfoCircle className="h-4 w-4" aria-hidden="true" />
          </button>
        </Tooltip>
      )}

      {/* Keep Options mounted so Headless UI can restore trigger focus on close. */}
      {renderLayer(
        <div
          {...layerProps}
          hidden={!isOpen}
          className="fixed overflow-hidden z-30 rounded-md shadow-[0_18px_48px_rgba(0,0,0,0.72)]"
          data-publish-to-events-dropdown
          style={{
            ...layerProps.style,
            width: dropdownWidth,
          }}
        >
          <Listbox.Options
            static
            className={cx(
              'm-0 max-h-48 w-full list-none rounded-md',
              'border border-white/10 bg-gray-800 text-sm ring-1 ring-black/5 focus:outline-none',
              'overflow-y-auto scrollbar-thin scrollbar-thumb-gray-650 scrollbar-track-transparent',
            )}
          >
            {isLoadingOptions ? (
              <>
                {Array.from({ length: 3 }).map((_, index) => (
                  <div
                    key={`event-part-loader-${index}`}
                    className="flex h-10 items-center gap-2 px-4"
                    aria-hidden
                  >
                    <div className="h-5 w-5 shrink-0 animate-pulse rounded bg-gray-600" />
                    <div className="h-5 w-[100px] animate-pulse rounded bg-gray-600" />
                  </div>
                ))}
              </>
            ) : (
              options.map(option => (
                <Listbox.Option
                  value={option}
                  key={option.eventPartId ?? 'replay'}
                  className={({ active }) =>
                    cx(
                      'relative cursor-default select-none bg-spring-neutral-base hover:cursor-pointer',
                      {
                        'bg-spring-neutral-b5': active,
                        'bg-spring-neutral-base': !active,
                      },
                    )
                  }
                >
                  {({ active, selected }) => (
                    <div
                      className={cx('relative flex px-4 py-2.5', {
                        'bg-spring-neutral-b5': active,
                      })}
                    >
                      <div className="relative flex grow flex-row gap-2">
                        <EventPartTypeBadge type={option.eventPartType} />

                        <span className="flex min-w-0 w-full flex-col">
                          <span className="flex min-w-0 flex-row items-center justify-between gap-2 text-sm font-medium text-white">
                            <span className="flex min-w-0 items-center gap-2">
                              <span className="truncate">
                                {getEventPartOptionLabel(option)}
                              </span>
                              {option.kind === 'replay' && (
                                <NewBadge
                                  label={t('content-recording.new-tag')}
                                />
                              )}
                            </span>
                            {selected && (
                              <CheckboxIcon
                                className="ml-2 h-4 w-4 text-spring-neutral-b0"
                                aria-hidden="true"
                              />
                            )}
                          </span>
                        </span>
                      </div>
                    </div>
                  )}
                </Listbox.Option>
              ))
            )}
          </Listbox.Options>
        </div>,
      )}
    </div>
  );
};

const NewBadge = ({ label }: { label: string }) => (
  <span className="flex shrink-0 items-center justify-center rounded-full bg-spring-primary p-1 text-[10px] font-bold leading-[10px] text-white">
    {label}
  </span>
);

const EventPartTypeBadge = ({ type }: { type: VideoArea['type'] | null }) => {
  if (type === 'stage') return <Stage className="h-5 w-5 text-[#86EFAC]" />;
  if (type === 'session') return <Session className="h-5 w-5 text-[#7DD3FC]" />;

  return <ReplayIcon className="h-5 w-5 text-[#FFA366]" />;
};
